import React, { useState, useCallback, useEffect } from 'react';
import Cropper, { Area } from 'react-easy-crop';
import { motion } from 'framer-motion';

interface ImageCropModalProps {
  open: boolean;
  creatorId: string | null;
  image: string | null;
  onClose: () => void;
  onCropComplete: (creatorId: string, croppedImage: string) => void;
}

const ImageCropModal: React.FC<ImageCropModalProps> = ({ open, creatorId, image, onClose, onCropComplete }) => {
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<Area | null>(null);

  // Add ESC key handler
  useEffect(() => {
    if (!open) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        onClose();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [open, onClose]);

  const handleCropComplete = useCallback((_: Area, croppedAreaPixels: Area) => {
    setCroppedAreaPixels(croppedAreaPixels);
  }, []);

  const getCroppedImg = useCallback(async () => {
    if (!image || !creatorId) return;
    const img = new window.Image();
    img.src = image;
    await new Promise(resolve => { img.onload = resolve; });
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    if (!ctx || !croppedAreaPixels) return;
    canvas.width = croppedAreaPixels.width;
    canvas.height = croppedAreaPixels.height;
    ctx.drawImage(
      img,
      croppedAreaPixels.x,
      croppedAreaPixels.y,
      croppedAreaPixels.width,
      croppedAreaPixels.height,
      0,
      0,
      croppedAreaPixels.width,
      croppedAreaPixels.height
    );
    onCropComplete(creatorId, canvas.toDataURL('image/jpeg'));
    onClose();
  }, [croppedAreaPixels, image, creatorId, onClose, onCropComplete]);

  if (!open) return null;

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0, scale: 0.95 }}
      transition={{ duration: 0.2 }}
      className="fixed inset-0 z-50 flex items-center justify-center"
      style={{ backgroundColor: 'var(--modal-backdrop)' }}
      onClick={onClose}
    >
      <div 
        className="theme-surface p-6 rounded-lg max-w-md w-full mx-4"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-semibold">Crop Image</h3>
          <p className="text-xs theme-text-muted">
            Press <kbd className="px-1 py-0.5 theme-muted border theme-border rounded text-xs">ESC</kbd> to cancel
          </p>
        </div>
        <div
          className="relative w-full h-64 theme-muted mb-4"
          onWheel={(e: React.WheelEvent<HTMLDivElement>) => {
            e.preventDefault();
            const delta = -e.deltaY || 0;
            setZoom(z => Math.min(Math.max(z + delta * 0.001, 0.1), 10));
          }}
        >
          <Cropper
            image={image || ''}
            crop={crop}
            zoom={zoom}
            aspect={1}
            minZoom={0.1}
            maxZoom={10}
            onCropChange={setCrop}
            onZoomChange={setZoom}
            onCropComplete={handleCropComplete}
          />
          <p className="text-sm theme-text-muted text-center mt-2">Scroll to Zoom. Drag to Move.</p>
        </div>
        <div className="flex justify-end items-center mb-2">
          <button
            onClick={() => {
              setCrop({ x: 0, y: 0 });
              setZoom(1);
            }}
            className="px-3 py-1 bg-green-600 text-white rounded hover:bg-green-700 text-sm"
          >
            Reset
          </button>
        </div>
        <div className="flex justify-end space-x-2">
          <button
            onClick={onClose}
            className="px-4 py-2 theme-text-muted hover:theme-text"
          >
            Cancel
          </button>
          <button
            onClick={getCroppedImg}
            className="px-4 py-2 bg-[var(--accent-primary)] text-white rounded border border-[color-mix(in_srgb,var(--accent-primary) 65%, transparent)] hover:bg-[color-mix(in_srgb,var(--accent-primary) 85%, #ffffff 15%)]"
          >
            Crop
          </button>
        </div>
      </div>
    </motion.div>
  );
};

export default ImageCropModal; 